/**
 * 행동발달 '학생 특성' 글자(prompt)와 특성 칩 사이의 선택 상태 계산.
 *
 * 선택 상태를 따로 저장하지 않는다. 교사가 직접 고친 글자가 언제나 기준이고,
 * 칩은 쉼표·줄바꿈으로 나뉜 조각 중 글자가 똑같은 것이 있으면 '고른 상태'다.
 */

const DELIMITER_SPLIT = /([,，\n])/;
const DELIMITER_TEST = /[,，\n]/;
const isComma = (token) => token === ',' || token === '，';

export const USAGE_STORAGE_PREFIX = 'wq_behavior_chip_usage_v1';
const USAGE_MAX_KEYS = 300;

export const parsePromptPieces = (prompt) => String(prompt || '')
  .split(DELIMITER_TEST)
  .map((piece) => piece.trim())
  .filter(Boolean);

export const isTraitSelected = (prompt, text) => {
  const value = String(text || '').trim();
  if (!value) return false;
  if (DELIMITER_TEST.test(value)) return String(prompt || '').includes(value);
  return parsePromptPieces(prompt).includes(value);
};

export const addTrait = (prompt, text) => {
  const value = String(text || '').trim();
  const current = String(prompt || '');
  if (!value || isTraitSelected(current, value)) return current;
  const base = current.replace(/[\s,，]+$/, '');
  return base ? `${base}, ${value}` : value;
};

const removeOnce = (tokens, index) => {
  const next = [...tokens];
  if (index > 0 && isComma(next[index - 1])) {
    next.splice(index - 1, 2);
  } else if (index + 1 < next.length && isComma(next[index + 1])) {
    next.splice(index, 2);
    if (typeof next[index] === 'string') next[index] = next[index].replace(/^[ \t]+/, '');
  } else if (index > 0) {
    next.splice(index - 1, 2);
  } else if (index + 1 < next.length) {
    next.splice(index, 2);
  } else {
    next.splice(index, 1);
  }
  return next;
};

export const removeTrait = (prompt, text) => {
  const value = String(text || '').trim();
  const current = String(prompt || '');
  if (!value) return current;
  if (DELIMITER_TEST.test(value)) {
    const at = current.indexOf(value);
    if (at < 0) return current;
    return `${current.slice(0, at)}${current.slice(at + value.length)}`
      .replace(/,\s*,/g, ',')
      .replace(/^[\s,，]+|[\s,，]+$/g, '');
  }
  let tokens = current.split(DELIMITER_SPLIT);
  let index = tokens.findIndex((token, i) => i % 2 === 0 && token.trim() === value);
  while (index >= 0) {
    tokens = removeOnce(tokens, index);
    // splice 로 조각·구분자 짝이 함께 빠지므로 짝수 위치가 계속 조각이다.
    index = tokens.findIndex((token, i) => i % 2 === 0 && token.trim() === value);
  }
  return tokens.join('').replace(/^[\s,，]+/, '').replace(/[ \t,，]+$/, '');
};

export const toggleTrait = (prompt, text) => (
  isTraitSelected(prompt, text) ? removeTrait(prompt, text) : addTrait(prompt, text)
);

export const customChipKey = (groupLabel, text) => `custom:${groupLabel}:${text}`;

export const chipUsageKey = (chip, groupLabel) => (
  chip?.isCustom ? customChipKey(groupLabel, chip.text) : chip?.id
);

const usageStorageKey = (scope) => `${USAGE_STORAGE_PREFIX}:${scope || 'anon'}`;

export const readChipUsage = (scope, storage = typeof window !== 'undefined' ? window.localStorage : null) => {
  try {
    const raw = storage?.getItem(usageStorageKey(scope));
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
};

export const recordChipUsage = (scope, key, storage = typeof window !== 'undefined' ? window.localStorage : null) => {
  if (!key || !storage) return;
  try {
    const usage = readChipUsage(scope, storage);
    usage[key] = (Number(usage[key]) || 0) + 1;
    const entries = Object.entries(usage);
    const trimmed = entries.length > USAGE_MAX_KEYS
      ? Object.fromEntries(entries.sort((a, b) => b[1] - a[1]).slice(0, USAGE_MAX_KEYS))
      : usage;
    storage.setItem(usageStorageKey(scope), JSON.stringify(trimmed));
  } catch {
    // 저장 공간이 없거나 막혀 있어도 칩 고르기는 그대로 된다.
  }
};

const normalizeQuery = (value) => String(value || '').toLowerCase().replace(/\s+/g, '');

/**
 * 특성 찾기. 글자 앞부분이 같은 칩 → 글자 안에 들어 있는 칩 → 비슷한 말(aliases)로 찾은 칩 순서다.
 * '조용한 편'처럼 칩 글자를 품은 긴 말을 쳐도 칩을 찾는다.
 */
export const searchBehaviorTraits = (groups, query, limit = 20) => {
  const q = normalizeQuery(query);
  if (!q) return [];
  const best = new Map();
  groups.forEach((group, groupIndex) => {
    [...group.chips, ...(group.customs || []), ...(group.scenes || [])].forEach((item, itemIndex) => {
      const text = normalizeQuery(item.text);
      const aliases = (item.aliases || []).map(normalizeQuery).filter(Boolean);
      let score = -1;
      if (text.startsWith(q)) score = 0;
      else if (text.includes(q)) score = 1;
      else if (aliases.some((alias) => alias.includes(q))) score = 2;
      else if (q.length >= 2 && [text, ...aliases].some((word) => word.length >= 2 && q.includes(word))) score = 3;
      if (score < 0) return;
      // 장면은 특성 칩보다 길고 많아서 특성 칩 결과를 모두 보인 뒤에 붙인다.
      if (item.type === 'scene') score += 10;
      const order = groupIndex * 1000 + itemIndex;
      const previous = best.get(item.text);
      if (!previous || score < previous.score) best.set(item.text, { item, group, score, order });
    });
  });
  return [...best.values()]
    .sort((a, b) => a.score - b.score || a.order - b.order)
    .slice(0, limit);
};

/**
 * 접힌 줄에 보일 칩을 고른다.
 * 1) 이 교사가 2번 이상 고른 칩(많이 쓴 순) 2) 교사가 만든 내 칩 3) 그룹 기본 칩(잘하는 점 N개, 도와줄 점 M개)
 * 4) 위에 없지만 지금 골라 둔 칩은 뒤에 붙여서 바로 뺄 수 있게 한다.
 * usage 는 페이지를 열 때 한 번 읽은 값을 넘겨서, 쓰는 도중에는 칩 자리가 바뀌지 않게 한다.
 */
export const buildCollapsedChips = ({
  group,
  customs = [],
  slots = [3, 1],
  usage = {},
  selectedTexts = new Set(),
}) => {
  const [positiveSlots, negativeSlots] = slots;
  const total = positiveSlots + negativeSlots;
  const pool = [...group.chips, ...customs];
  const keyOf = (item) => chipUsageKey(item, group.label);
  const picks = [];
  const has = (item) => picks.some((pick) => pick.text === item.text);
  const push = (item) => { if (picks.length < total && !has(item)) picks.push(item); };

  pool
    .filter((item) => (Number(usage[keyOf(item)]) || 0) >= 2)
    .sort((a, b) => (Number(usage[keyOf(b)]) || 0) - (Number(usage[keyOf(a)]) || 0))
    .forEach(push);
  customs.forEach(push);
  const positives = group.chips.filter((item) => item.type === 'positive');
  const negatives = group.chips.filter((item) => item.type === 'negative');
  [
    ...positives.slice(0, positiveSlots),
    ...negatives.slice(0, negativeSlots),
    ...positives.slice(positiveSlots),
    ...negatives.slice(negativeSlots),
  ].forEach(push);

  const extras = pool.filter((item) => selectedTexts.has(item.text) && !has(item));
  return { visible: picks, extras, hiddenCount: Math.max(0, pool.length - picks.length - extras.length) };
};
